import { useRef } from 'react'
import { GripVertical, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui'
import { cn } from '@/lib/utils/cn'
import type { Formulation } from '../api/types'

/**
 * Editable procedure list (Sprint 08b — Item 2).
 *
 * Replaces the read-only numbered list that shipped in Sprint 08
 * narrow v1. Each step is a row with a drag handle (left), an
 * auto-incrementing step number (rendered, not editable), an
 * auto-growing textarea (text content), and a trash icon (right).
 * Add Step button below the list. Empty steps stripped on parent
 * save (the parent's toWritePayload handles trimming).
 *
 * Reorder uses native HTML5 drag — same pattern as EditableGrid's
 * row drag (no @dnd-kit dependency for this single use case).
 *
 * Step numbers in the rendered output are always 1..N (auto-renumbered
 * on add/remove/reorder); imported step values are preserved on the
 * underlying procedure array but not displayed since auto-numbering is
 * the source of truth for v2.
 */

type ProcedureStep = NonNullable<Formulation['procedure']>[number]

export interface ProcedureEditorProps {
  procedure: ProcedureStep[]
  onChange: (next: ProcedureStep[]) => void
  readOnly?: boolean
}

export function ProcedureEditor({
  procedure,
  onChange,
  readOnly,
}: ProcedureEditorProps) {
  const dragIndexRef = useRef<number | null>(null)

  const updateStep = (index: number, text: string) => {
    const next = procedure.map((step, i) =>
      i === index ? { ...step, text } : step,
    )
    onChange(next)
  }

  const addStep = () => {
    const next: ProcedureStep[] = [
      ...procedure,
      { step: procedure.length + 1, text: '' },
    ]
    onChange(next)
  }

  const removeStep = (index: number) => {
    const next = procedure.filter((_, i) => i !== index).map((step, i) => ({
      ...step,
      step: i + 1,
    }))
    onChange(next)
  }

  const moveStep = (from: number, to: number) => {
    if (from === to) return
    const next = [...procedure]
    const [moved] = next.splice(from, 1)
    if (!moved) return
    next.splice(to, 0, moved)
    onChange(next.map((step, i) => ({ ...step, step: i + 1 })))
  }

  return (
    <div className="flex flex-col gap-2">
      {procedure.length === 0 ? (
        <p className="text-sm italic text-tertiary">
          No procedure steps. Add one below or import a template.
        </p>
      ) : (
        <ol className="flex flex-col gap-1">
          {procedure.map((step, index) => (
            <li
              key={index}
              draggable={!readOnly}
              onDragStart={() => {
                dragIndexRef.current = index
              }}
              onDragOver={readOnly ? undefined : (e) => e.preventDefault()}
              onDrop={
                readOnly
                  ? undefined
                  : () => {
                      const from = dragIndexRef.current
                      if (from !== null && from !== index) {
                        moveStep(from, index)
                      }
                      dragIndexRef.current = null
                    }
              }
              className={cn(
                'flex items-start gap-2 rounded-sm border border-border-subtle bg-surface px-2 py-1.5',
              )}
            >
              {readOnly ? null : (
                <span
                  aria-label="Drag to reorder"
                  className="mt-1.5 flex h-5 w-4 cursor-grab items-center justify-center text-tertiary hover:text-primary active:cursor-grabbing"
                >
                  <GripVertical size={12} />
                </span>
              )}
              <span className="text-role-metadata mt-1.5 inline-flex h-5 w-6 flex-shrink-0 items-center justify-center text-secondary tabular-nums">
                {index + 1}.
              </span>
              <textarea
                value={step.text}
                onChange={(e) => updateStep(index, e.target.value)}
                readOnly={readOnly}
                rows={Math.max(1, Math.ceil(step.text.length / 80))}
                className={cn(
                  'flex-1 resize-y border-0 bg-transparent p-1 text-sm text-primary outline-none focus:bg-surface-sunk focus:ring-1 focus:ring-accent',
                  readOnly && 'cursor-default',
                )}
                placeholder="Step description…"
              />
              {readOnly ? null : (
                <button
                  type="button"
                  onClick={() => removeStep(index)}
                  aria-label={`Remove step ${index + 1}`}
                  title="Remove step"
                  className="mt-1 flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-danger-bg hover:text-danger-text"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </li>
          ))}
        </ol>
      )}
      {readOnly ? null : (
        <Button variant="ghost" size="sm" onClick={addStep}>
          <Plus size={14} />
          Add step
        </Button>
      )}
    </div>
  )
}
